/**
 * Where to put the label of a highlighted region (Issue #2254).
 *
 * The rules are the ones the walkthrough video renderer uses for its tags
 * (`tools/video-story/video_story/chrome.py`, `tag_position` and `place_tags`), so a region is
 * labelled in the same place in the app as in the video:
 *
 * 1. A label sits beside its own box. Six positions are tried, in this order: above the box at
 *    its left edge, below at its left edge, to the right, to the left, above at its right edge,
 *    below at its right edge.
 * 2. Each position is first moved inside the drawing, so a label is never cut off at an edge.
 * 3. The position that covers the fewest highlighted boxes and already-placed labels wins. The
 *    earlier position wins a tie.
 * 4. Labels are placed one after another, and each placed label becomes an obstacle for the next,
 *    so two labels never choose the same spot.
 *
 * Three differences from the video renderer, all deliberate:
 *
 * - A region inside a larger region (a detail inside the room it belongs to) is labelled beside
 *   its own box, on top of the larger region. There is nowhere else to put it that still reads as
 *   its label.
 * - The video also prefers the position that covers the least drawing ink. The app does not read
 *   the drawing's pixels here, because reading a large canvas back from the GPU while the user
 *   pans and zooms is not free. Left as a follow-up on the issue.
 * - If all six positions collide, the app tries a few positions further above and below the box
 *   before giving up. The video renderer has a human reviewing every frame; a deep link does not.
 *
 * Pure geometry: no Angular and no DOM, so it is tested directly.
 */

/** A rectangle in the overlay's coordinate space (canvas display pixels). */
export interface LabelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The size of a label, or of the area labels must stay inside. */
export interface LabelSize {
  width: number;
  height: number;
}

/** Spacing rules for label placement. */
export interface LabelPlacementOptions {
  /** Distance between a box and its label. */
  gap: number;
  /** Smallest distance between a label and the edge of `bounds`. */
  margin: number;
  /** The area labels must stay inside. When it is unknown or empty, labels are not moved. */
  bounds?: LabelSize | null;
}

/** A region to label: its box, and the size of its label (`null` when it has no label). */
export interface LabelRequest {
  box: LabelRect;
  size: LabelSize | null;
}

/** Label text size, in screen pixels. The overlay's stylesheet sets the same size. */
export const LABEL_FONT_PX = 13;

/** Space between a label's edge and its text, on each side. */
export const LABEL_PADDING_X_PX = 10;

/**
 * The width of a label for the given text, in screen pixels, padding included.
 *
 * A label is drawn in the overlay's SVG, so it scales with the drawing during a pinch gesture,
 * and its width has to be known before it is placed. Measuring text needs a rendered element,
 * and placement runs before anything is rendered, so the width is estimated from the characters.
 *
 * The figures are the advance widths of the label font (Roboto 600), measured in Chromium and
 * rounded up within each group of characters, so the estimate errs on the wide side: a label is
 * never narrower than its text.
 */
export function estimateLabelWidth(text: string | undefined): number {
  let em = 0;
  for (const char of text ?? '') {
    if (char === ' ') em += 0.26;
    else if (NARROW_CHARACTERS.includes(char)) em += 0.39;
    else if (WIDE_CHARACTERS.includes(char)) em += 0.93;
    else if (char >= 'A' && char <= 'Z') em += 0.7;
    else em += 0.58;
  }
  return Math.ceil(em * LABEL_FONT_PX) + LABEL_PADDING_X_PX * 2;
}

const NARROW_CHARACTERS = 'iIljtfr.,:;\'"|!()[]·-/';
const WIDE_CHARACTERS = 'mwMW@%—…';

/** How many extra rows above and below the box are tried when the six positions all collide. */
const FALLBACK_ROWS = 3;

/** The six positions beside a box, in the video renderer's order of preference. */
export function labelCandidates(box: LabelRect, size: LabelSize, gap: number): { x: number; y: number }[] {
  const left = box.x;
  const top = box.y;
  const right = box.x + box.width;
  const bottom = box.y + box.height;
  const middle = (top + bottom - size.height) / 2;
  return [
    { x: left, y: top - size.height - gap },
    { x: left, y: bottom + gap },
    { x: right + gap, y: middle },
    { x: left - size.width - gap, y: middle },
    { x: right - size.width, y: top - size.height - gap },
    { x: right - size.width, y: bottom + gap },
  ];
}

/** True when two rectangles share any area. Rectangles that only touch do not overlap. */
export function rectsOverlap(a: LabelRect, b: LabelRect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/**
 * Places one label beside its box, avoiding the rectangles in `occupied`.
 *
 * `occupied` should include the label's own box: a position that had to be moved inside the
 * drawing can end up on top of the box it describes, and that must count against it.
 */
export function placeLabel(
  box: LabelRect,
  size: LabelSize,
  occupied: readonly LabelRect[],
  options: LabelPlacementOptions
): LabelRect {
  const collisions = (rect: LabelRect): number => occupied.filter(other => rectsOverlap(rect, other)).length;
  const toRect = (point: { x: number; y: number }): LabelRect => clampToBounds({ ...point, ...size }, options);

  let best: LabelRect | null = null;
  let bestCollisions = Number.POSITIVE_INFINITY;
  for (const candidate of labelCandidates(box, size, options.gap)) {
    const rect = toRect(candidate);
    const count = collisions(rect);
    if (count < bestCollisions) {
      best = rect;
      bestCollisions = count;
    }
  }
  if (best && bestCollisions === 0) {
    return best;
  }

  // Every position beside the box collides. Look further out, above and then below, one label
  // height at a time, and take the first free spot.
  const step = size.height + options.gap;
  for (let row = 1; row <= FALLBACK_ROWS; row++) {
    const above = toRect({ x: box.x, y: box.y - options.gap - size.height - row * step });
    if (collisions(above) === 0) {
      return above;
    }
    const below = toRect({ x: box.x, y: box.y + box.height + options.gap + row * step });
    if (collisions(below) === 0) {
      return below;
    }
  }
  return best ?? toRect({ x: box.x, y: box.y });
}

/**
 * Places the labels of several regions. The result has one entry per request, in the same order;
 * a region without a label gets `null`.
 */
export function placeLabels(requests: readonly LabelRequest[], options: LabelPlacementOptions): (LabelRect | null)[] {
  // Every highlighted box is an obstacle from the start, whether or not it has a label.
  const boxes: LabelRect[] = requests.map(request => request.box);
  const placedLabels: LabelRect[] = [];
  return requests.map(request => {
    if (!request.size) {
      return null;
    }
    // A region drawn inside a larger one (a detail inside the room it belongs to) can only be
    // labelled on top of the larger region. Counting that region as an obstacle would push the
    // label away from the box it describes, so the regions that contain this one are left out.
    const obstacles = boxes.filter(box => box === request.box || !rectContains(box, request.box));
    const placed = placeLabel(request.box, request.size, [...obstacles, ...placedLabels], options);
    placedLabels.push(placed);
    return placed;
  });
}

/** True when `outer` covers all of `inner`. A rectangle contains an identical one. */
export function rectContains(outer: LabelRect, inner: LabelRect): boolean {
  return (
    outer.x <= inner.x &&
    outer.y <= inner.y &&
    outer.x + outer.width >= inner.x + inner.width &&
    outer.y + outer.height >= inner.y + inner.height
  );
}

/** Moves a rectangle inside the bounds, keeping `margin` from each edge. */
function clampToBounds(rect: LabelRect, options: LabelPlacementOptions): LabelRect {
  const bounds = options.bounds;
  if (!bounds || !(bounds.width > 0) || !(bounds.height > 0)) {
    return { ...rect, x: Math.round(rect.x), y: Math.round(rect.y) };
  }
  // When the label is wider than the drawing, keep its left edge visible rather than its right.
  const maxX = Math.max(options.margin, bounds.width - rect.width - options.margin);
  const maxY = Math.max(options.margin, bounds.height - rect.height - options.margin);
  return {
    ...rect,
    x: Math.round(Math.min(Math.max(options.margin, rect.x), maxX)),
    y: Math.round(Math.min(Math.max(options.margin, rect.y), maxY)),
  };
}
